import axios from "axios";
import { GET_TIMEOUT_MS, isTransientNetworkError } from "@/services/api.service";
import { waitBeforeRetry } from "@/services/retry-backoff";

/**
 * The freshness matrix accepted on 2026-09-14 and relaxed on 2026-10-15
 * (NG-3012), as code. The numbers are the contract's
 * (`docs/design/frontend-caching-pilot-contract.md` §2 and §2.2); change them
 * there first.
 *
 * - **access-sensitive**: who may see or do what. Reused for 60 s, then re-read
 *   on mount, focus or reconnect; never on an interval. A write that changes a
 *   person's access sends them an `access:self` hint
 *   (`stores/cache-hints/access.ts`), which re-reads these at once.
 * - **mutable**: working data. Reused for 60 s, refreshed every 60 s while a
 *   visible screen observes it, which is how another person's write shows
 *   when no hint covers it.
 * - **reference**: display data that rarely changes. Reused for 5 min.
 *
 * A data class missing from the matrix is treated as access-sensitive until it
 * is added (contract §7), which is why `client.ts` uses that row as its
 * default.
 */
export type FreshnessClass = "access-sensitive" | "mutable" | "reference";

const SECOND = 1_000;
const MINUTE = 60 * SECOND;

export interface FreshnessPolicy {
  staleTime: number;
  gcTime: number;
  /** A visible refresh; never runs in a background tab. */
  refetchInterval: number | false;
  refetchIntervalInBackground: false;
  refetchOnMount: boolean | "always";
  refetchOnWindowFocus: boolean;
  refetchOnReconnect: boolean;
  /** Read by the client's cache lifecycle; see `freshnessOf`. */
  meta: { freshness: FreshnessClass };
}

export const FRESHNESS: Readonly<Record<FreshnessClass, Readonly<FreshnessPolicy>>> = {
  "access-sensitive": {
    staleTime: 60 * SECOND,
    gcTime: 5 * MINUTE,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: { freshness: "access-sensitive" },
  },
  mutable: {
    staleTime: 60 * SECOND,
    gcTime: 5 * MINUTE,
    refetchInterval: 60 * SECOND,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: { freshness: "mutable" },
  },
  reference: {
    staleTime: 5 * MINUTE,
    gcTime: 15 * MINUTE,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    meta: { freshness: "reference" },
  },
};

/** The freshness class a query was declared with, from its `meta`. */
export function freshnessOf(meta: Record<string, unknown> | undefined): FreshnessClass | undefined {
  const freshness = meta?.freshness;
  return freshness === "access-sensitive" || freshness === "mutable" || freshness === "reference"
    ? freshness
    : undefined;
}

/**
 * Retry ownership (contract §6.2). A query-owned read has exactly one retry
 * owner, this policy:
 *
 * - at most {@link MAX_SAFE_READ_RETRIES} retries, and only for a read its
 *   definition declares safe (`retry: "safe-read"`);
 * - all of it inside {@link INTERACTIVE_DEADLINE_MS} from the first attempt, so
 *   a skeleton never outlives the deadline: each attempt's Axios timeout is
 *   capped to what is left of it. A read that walks every page of a list
 *   ({@link withinAttempt}) may pass a longer deadline, up to the GET timeout
 *   (`GET_TIMEOUT_MS`), when its walk is known to outlast ten seconds;
 * - none on a 4xx, a cancellation or a timeout;
 * - never on a mutation (the client turns mutation retry off).
 *
 * TanStack Query's own retry is off for every query (`client.ts`) and the
 * Axios transient-GET retry is off for every request a read makes
 * (`transientRetry: false`). The loop lives here rather than in Query's
 * `retry` callback because that callback sees neither the elapsed time nor the
 * request timeout, and the deadline needs both.
 */
export const MAX_SAFE_READ_RETRIES = 2;
export const INTERACTIVE_DEADLINE_MS = 10 * SECOND;
const RETRY_BASE_DELAY_MS = 300;
/** Too little left for an attempt to finish is not worth starting one. */
const MIN_ATTEMPT_MS = 1 * SECOND;

export type RetryPolicy = "safe-read" | "none";

/**
 * What a read passes to its service: `ApiService.get` accepts it as config.
 * The signal is Query's, so a cancelled query aborts the Axios request.
 */
export interface ReadRequest {
  signal: AbortSignal;
  timeout: number;
  transientRetry: false;
}

/**
 * How long to wait before retrying `error`, or `null` when it must not be
 * retried. Transient means a dropped connection or a 502/503/504; a 429 waits
 * out its `Retry-After`. A cancel, a timeout, any other HTTP answer and any
 * non-Axios error are final.
 */
export function retryDelayMs(retry: number, error: unknown): number | null {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return null;
  const backoff = RETRY_BASE_DELAY_MS * 2 ** retry + Math.random() * (RETRY_BASE_DELAY_MS / 2);
  if (isTransientNetworkError(error)) return backoff;
  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) return backoff;
  if (status === 429) {
    const seconds = Number(error.response?.headers?.["retry-after"]);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds * SECOND : backoff;
  }
  return null;
}

/**
 * Run one read under the retry policy. `signal` is the query's: aborting it
 * aborts the in-flight request and ends a pending backoff. `deadlineMs` is the
 * whole read's budget, retries included.
 */
export async function runRead<T>(
  read: (request: ReadRequest) => Promise<T>,
  signal: AbortSignal,
  policy: RetryPolicy,
  deadlineMs: number = INTERACTIVE_DEADLINE_MS,
): Promise<T> {
  const deadline = Date.now() + deadlineMs;
  for (let retry = 0; ; retry++) {
    const remaining = deadline - Date.now();
    try {
      return await read({
        signal,
        timeout: Math.max(1, Math.min(GET_TIMEOUT_MS, remaining)),
        transientRetry: false,
      });
    } catch (error) {
      const delay =
        policy === "safe-read" && retry < MAX_SAFE_READ_RETRIES ? retryDelayMs(retry, error) : null;
      // Final: the error propagates to the query, whose cache logs it (client.ts).
      if (delay === null || Date.now() + delay > deadline - MIN_ATTEMPT_MS) throw error;
      // Recorded even when the retry recovers, so a flaky endpoint stays visible.
      console.warn(
        `[queries] safe read failed; retry ${retry + 1}/${MAX_SAFE_READ_RETRIES} in ${Math.round(delay)}ms`,
        error,
      );
      if (!(await waitBeforeRetry(delay, signal))) throw error;
    }
  }
}

/**
 * Run a multi-request read with `request.timeout` as its total budget: the
 * signal handed on aborts when the query's does or when the budget runs out.
 * A read that walks every page of a list uses it, so the whole walk, not each
 * page, fits inside one attempt.
 */
export async function withinAttempt<T>(
  request: ReadRequest,
  run: (bounded: ReadRequest) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (request.signal.aborted) abort();
  request.signal.addEventListener("abort", abort);
  const timer = setTimeout(abort, request.timeout);
  try {
    return await run({ ...request, signal: controller.signal });
  } finally {
    clearTimeout(timer);
    request.signal.removeEventListener("abort", abort);
  }
}
